import { useState } from 'react';
import MetricCard from '../MetricCard';
import ChartCard from '../charts/ChartCard';
import CustomTooltip from '../charts/CustomTooltip';
import TimePeriodToggle from './TimePeriodToggle';
import { formatNumber } from '../../utils/formatters';
import { groupByTimePeriod } from '../../utils/timePeriod';
import { CHART_COLORS, STATUS_COLORS, CHART_THEME } from '../../utils/chartColors';
import {
  FiDollarSign,
  FiTrendingUp,
  FiUsers,
  FiClock,
  FiPercent,
  FiZap
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const OverviewTab = ({ data }) => {
  const [timePeriod, setTimePeriod] = useState('monthly');

  if (!data) return null;

  const { overview, growth, timeSeries } = data;

  // MRR trend data (always monthly)
  const mrrTrend = growth?.mrrTrend || [];

  // Grouped time series for sub growth + revenue charts
  const grouped = groupByTimePeriod(timeSeries || [], timePeriod);

  const growthData = grouped.map(d => ({
    period: d.period,
    new: d.newSubs,
    canceled: d.canceledSubs,
  }));

  const revenueVsRefunds = grouped.map(d => ({
    period: d.period,
    collected: d.collected,
    refunded: d.refunded,
  }));

  // Status distribution
  const statusData = [
    { name: 'Active', value: overview.activeOnly || overview.activeSubscriptions, color: STATUS_COLORS.active },
    { name: 'Trialing', value: overview.trialingSubscriptions, color: STATUS_COLORS.trialing },
    { name: 'Canceled', value: overview.canceledSubscriptions, color: STATUS_COLORS.canceled },
    { name: 'Past Due', value: overview.pastDueSubscriptions, color: STATUS_COLORS.past_due },
    { name: 'Incomplete', value: overview.incompleteSubscriptions, color: STATUS_COLORS.incomplete },
  ].filter(d => d.value > 0);

  const totalStatusCount = statusData.reduce((sum, d) => sum + d.value, 0);
  const currencyFormatter = (val) => `$${val.toLocaleString()}`;
  const isDailyView = timePeriod === 'daily';

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard label="MRR" value={`$${formatNumber(overview.mrr)}`} icon={FiDollarSign} />
        <MetricCard label="ARR" value={`$${formatNumber(overview.arr)}`} icon={FiTrendingUp} />
        <MetricCard label="Active Subs" value={formatNumber(overview.activeSubscriptions)} icon={FiUsers} />
        <MetricCard label="Trialing" value={formatNumber(overview.trialingSubscriptions)} icon={FiClock} />
        <MetricCard label="Churn Rate" value={`${overview.churnRate}%`} icon={FiPercent} />
        <MetricCard label="Net Revenue" value={`$${formatNumber(overview.netRevenue)}`} icon={FiZap} />
      </div>

      {/* Time Period Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Trends</h3>
        <TimePeriodToggle value={timePeriod} onChange={setTimePeriod} />
      </div>

      {/* Charts 2x2 Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* MRR Trend (always monthly) */}
        <ChartCard title="MRR Trend" subtitle="Monthly recurring revenue over last 12 months">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={mrrTrend}>
              <defs>
                <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="month" tick={CHART_THEME.axis} />
              <YAxis tick={CHART_THEME.axis} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip formatter={currencyFormatter} />} />
              <Area
                type="monotone"
                dataKey="mrr"
                stroke={CHART_COLORS.primary}
                strokeWidth={2}
                fill="url(#mrrGradient)"
                name="MRR"
                dot={false}
                activeDot={{ r: 4, fill: CHART_COLORS.primary }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Subscription Growth */}
        <ChartCard title="Subscription Growth" subtitle={`New vs canceled subscriptions — ${timePeriod}`}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={growthData} barGap={2}>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="period" tick={CHART_THEME.axis} angle={isDailyView ? -45 : 0} textAnchor={isDailyView ? 'end' : 'middle'} height={isDailyView ? 50 : 30} />
              <YAxis tick={CHART_THEME.axis} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} />
              <Bar dataKey="new" fill={CHART_COLORS.success} name="New" radius={[3, 3, 0, 0]} barSize={isDailyView ? 10 : 20} />
              <Bar dataKey="canceled" fill={CHART_COLORS.danger} name="Canceled" radius={[3, 3, 0, 0]} barSize={isDailyView ? 10 : 20} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Revenue vs Refunds */}
        <ChartCard title="Revenue vs Refunds" subtitle={`Collected revenue with refund overlay — ${timePeriod}`}>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={revenueVsRefunds}>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="period" tick={CHART_THEME.axis} angle={isDailyView ? -45 : 0} textAnchor={isDailyView ? 'end' : 'middle'} height={isDailyView ? 50 : 30} />
              <YAxis tick={CHART_THEME.axis} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip formatter={currencyFormatter} />} />
              <Legend iconType="circle" iconSize={8} />
              <Bar dataKey="collected" fill={CHART_COLORS.info} name="Revenue" radius={[3, 3, 0, 0]} barSize={isDailyView ? 12 : 24} />
              <Line type="monotone" dataKey="refunded" stroke={CHART_COLORS.danger} name="Refunds" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: CHART_COLORS.danger }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Status Distribution */}
        <ChartCard title="Subscription Status" subtitle="Current distribution across all statuses">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={65} outerRadius={95} paddingAngle={3} strokeWidth={0}>
                {statusData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0];
                  return (
                    <div className="bg-slate-800 text-slate-50 px-3 py-2 rounded-lg shadow-lg text-xs">
                      <p className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.payload.color }} />
                        <span className="text-slate-300">{d.name}:</span>
                        <span className="font-semibold">{d.value}</span>
                        <span className="text-slate-400">({((d.value / totalStatusCount) * 100).toFixed(1)}%)</span>
                      </p>
                    </div>
                  );
                }}
              />
              <Legend iconType="circle" iconSize={8} />
              <text x="50%" y="47%" textAnchor="middle" className="text-2xl font-semibold fill-gray-900">{totalStatusCount}</text>
              <text x="50%" y="55%" textAnchor="middle" className="text-xs fill-gray-500">Total</text>
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Quick Stats Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="metric-card text-center">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Retention Rate</p>
          <p className="text-xl font-semibold text-green-600 tabular-nums">{overview.retentionRate}%</p>
        </div>
        <div className="metric-card text-center">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Avg Sub Value</p>
          <p className="text-xl font-semibold text-gray-900 tabular-nums">${formatNumber(overview.avgSubscriptionValue)}/mo</p>
        </div>
        <div className="metric-card text-center">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Collection Rate</p>
          <p className="text-xl font-semibold text-blue-600 tabular-nums">{overview.collectionRate}%</p>
        </div>
        <div className="metric-card text-center">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Total Customers</p>
          <p className="text-xl font-semibold text-gray-900 tabular-nums">{formatNumber(overview.totalCustomers)}</p>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
